import { z } from 'zod/v4';

import { partialHoverEffectsSchema, partialMenuItemSchema } from '../menu-shared/menu-item.schema';

/**
 * THE menu widget data shape as AI tools accept it — flat, all-optional, shared by
 * `create_menu_widget`/`update_menu_widget`. Same menu-list/typography/layout/hover fields as
 * `navigation` (see `partial-navigation-widget-data.schema.ts`), minus one group and one renaming:
 *
 * - **The embedded logo is NOT exposed** (`isLogo`/`uploadImage`/`logoPosition`/`logoWidth`).
 *   It duplicates the standalone `logo` widget with its own fields, and a header already gets a
 *   logo from that widget placed beside `navigation` — reusing the image-import pipeline for a
 *   second, nested call site was judged not worth it for a feature the app itself treats as
 *   secondary. Revisit if a real need for a `menu`-with-logo (rather than `logo` + `navigation`)
 *   shows up.
 * - **`fontStyle`/`submenuFontStyle` here, not `fontStile`/`submenuFontStile`.** The persisted
 *   field name IS misspelled (`menuLegacyDataSchema` in `@sites/entity-core` keeps it verbatim,
 *   since that is what real data parses against) — but a model has no reason to know that, and the
 *   correctly-spelled name is exactly what `navigation`'s identical-looking field is called. The
 *   fold in `menu-widget-patch.ts` renames on the way in.
 */
export const partialMenuWidgetDataSchema = z.object({
    menuList: z
        .array(partialMenuItemSchema)
        .optional()
        .describe('The menu entries, left to right (or top to bottom for layoutDirection "Vertical").'),
    textColor: z.string().optional().describe('Menu item text color.'),
    submenuTextColor: z.string().optional().describe('Submenu (dropdown) item text color.'),
    fontSize: z.number().int().positive().optional().describe('Menu item font size in pixels.'),
    submenuFontSize: z.number().int().positive().optional().describe('Submenu item font size in pixels.'),
    menuItemIndent: z.enum(['S', 'M', 'L']).optional().describe('Spacing between menu items.'),
    horizontalPosition: z
        .enum(['L', 'M', 'R'])
        .optional()
        .describe('Where the whole menu sits in its column: left, center, or right.'),
    fontStyle: z
        .array(z.enum(['I', 'B', 'AT']))
        .optional()
        .describe('Menu item text style flags: "I" italic, "B" bold, "AT" all-caps. Empty array for plain text.'),
    submenuFontStyle: z
        .array(z.enum(['I', 'B', 'AT']))
        .optional()
        .describe('Same style flags for submenu items.'),
    submenuListBackground: z.string().optional().describe('Background color of an open dropdown.'),
    layoutDirection: z.enum(['Horizontal', 'Vertical']).optional().describe('Menu items in a row or a column.'),
    showIconOnMobile: z
        .boolean()
        .optional()
        .describe('Collapse into a burger icon on mobile instead of showing the full menu.'),
    iconColor: z.string().optional().describe('Burger icon color.'),
    iconPosition: z.enum(['L', 'M', 'R']).optional().describe('Burger icon position.'),
    iconSize: z.enum(['S', 'M', 'L']).optional().describe('Burger icon size.'),
    hoverEffects: partialHoverEffectsSchema
        .optional()
        .describe('What happens to a menu item on hover. Partial — only the keys you provide change.'),
    isSynchronizeStyle: z
        .boolean()
        .optional()
        .describe('Whether tablet/mobile styling stays synchronized with the desktop styling.'),
    hideOnDesktop: z.boolean().optional().describe('Hide this widget on desktop screens.'),
    hideOnTablet: z.boolean().optional().describe('Hide this widget on tablet screens.'),
    hideOnMobile: z.boolean().optional().describe('Hide this widget on mobile screens.'),
});

export type PartialMenuWidgetData = z.infer<typeof partialMenuWidgetDataSchema>;
